import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import {
  NarratorGender,
  NarratorVoice,
  type components,
  VoiceCloneCreateRequestFileExtension,
} from '@storees/api-types';
import { environment } from '@storees/environments';
import {
  VOICE_CLONE_REQUESTS_ROUTE,
  VOICE_CLONE_SLOT_DAD_ID,
  VOICE_CLONE_SLOT_MOM_ID,
  VOICE_CLONES_ROUTE,
  isNamedCustomVoiceCloneId,
  tryParseNarratorUuidFromVoiceCloneCustomSlotId,
  voiceCloneCustomNarratorIdFromNarratorUuid,
} from '@storees/shared-constants';
import {
  VoiceCloneConnectedResponse,
  VoiceCloneSampleFailedResponse,
  VoiceCloneSampleReadyResponse,
  VoiceCloneWebSocketEnvelope,
  VoiceCloneWebSocketMessageType,
} from '@storees/models';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import urlJoin from 'url-join';
import { logger } from '../utils/logger';
import {
  resolveVoiceCloneNarratorAvatarUrl,
  stableVoiceCloneAvatarUrlForNarrator,
} from '../utils/voice-clone-narrator-avatar';
import { AuthService } from './auth-service';
import { DexieNarratorsService } from './dexie/dexie-narrators.service';
import { SessionService } from './session.service';
import {
  VoiceCloneFlowService,
  type VoiceCloneFlowContext,
} from './voice-clone-flow.service';
import { StoreesBackendService, VoiceCloneTrialState } from './storees-backend.service';
import { StoreesSubscriptionService } from './storees-subscription-service';
import { VoiceCloneSocketService } from './voice-clone-socket.service';
import { VoiceRecordingService } from './voice-recording.service';

const { apiBaseUrl, webAppBaseUrl } = environment;

const VOICE_CLONE_CREATED_KEYS = 'voice_clone_created_voices';
const VOICE_CLONE_DISPLAY_NAMES_KEY = 'voice_clone_display_names';
const VOICE_CLONE_AVATAR_URLS_KEY = 'voice_clone_avatar_image_urls';
/** Finalized narrator UUID → `voice-clone-custom-*` slot (stable local recording key; never derives from renamed display title). */
const VOICE_CLONE_NARRATOR_NAMED_SLOT_KEY = 'voice_clone_narrator_named_slot';

type VoiceClone = components['schemas']['VoiceClone'];
type VoiceCloneCreateRequest = components['schemas']['VoiceCloneCreateRequest'];
type VoiceCloneCreateResponse = components['schemas']['VoiceCloneCreateResponse'];
type Narrator = components['schemas']['Narrator'];
type VoiceCloneDownloadAsset = 'referenceAudioUrl' | 'previewCloneAudioUrl';
export enum VoiceCloneRequestStatus {
  Pending = 'Pending',
  Opened = 'Opened',
  Processing = 'Processing',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  Expired = 'Expired',
}

export interface VoiceCloneRequest {
  id: string;
  requestedName: string;
  requestedGender?: NarratorGender | null;
  requestedAvatarUrl?: string | null;
  requestMessage?: string | null;
  status: VoiceCloneRequestStatus;
  voiceCloneId?: string | null;
  previewDownloadUrl?: string | null;
  openedAt?: string | null;
  completedAt?: string | null;
  expiresAt: string;
  createdAt: string;
}

type VoiceCloneSocketMessage =
  | VoiceCloneWebSocketEnvelope<VoiceCloneConnectedResponse>
  | VoiceCloneWebSocketEnvelope<VoiceCloneSampleReadyResponse>
  | VoiceCloneWebSocketEnvelope<VoiceCloneSampleFailedResponse>;

type VoiceCloneSampleWaitPromise = Promise<VoiceCloneSampleReadyResponse> & {
  connected: Promise<void>;
};

export type VoiceClonePaywallType =
  | 'reverse-trial'
  | 'subscription-required'
  | 'bronze'
  | null;

export type VoiceCloneProcessingStatus =
  | 'idle'
  | 'creating'
  | 'uploading'
  | 'processing'
  | 'ready'
  | 'failed';

export interface VoiceCloneProcessingState {
  status: VoiceCloneProcessingStatus;
  voiceCloneId?: string;
  voiceName?: string;
  narratorId?: string;
  previewCloneAudioUrl?: string | null;
  error?: string;
}

export class VoiceCloneNoEntitlementError extends Error {
  constructor(message = 'No active voice clone access') {
    super(message);
    this.name = 'VoiceCloneNoEntitlementError';
  }
}

/** Public voice-clone request cannot accept a new clone (completed, expired, duplicate POST race, etc.). */
export class VoiceCloneRequestNotUsableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VoiceCloneRequestNotUsableError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class VoiceCloneService {
  private injector = inject(Injector);
  private sessionService = inject(SessionService);
  private voiceCloneFlowService = inject(VoiceCloneFlowService);
  private storeesSubscriptionService = inject(StoreesSubscriptionService);
  private trialStateCache: {
    value: VoiceCloneTrialState;
    fetchedAtMs: number;
    optimisticOverrideDetected: boolean;
  } | null = null;
  private optimisticOverrideDetected = false;

  private readonly processingStateSubject =
    new BehaviorSubject<VoiceCloneProcessingState>({
      status: 'idle',
    });
  private activeVoiceCloneNarrator?: Narrator;

  private get http(): HttpClient {
    return this.injector.get(HttpClient);
  }

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private get storeesBackendService(): StoreesBackendService {
    return this.injector.get(StoreesBackendService);
  }

  private get dexieNarratorsService(): DexieNarratorsService {
    return this.injector.get(DexieNarratorsService);
  }

  private get voiceCloneSocketService(): VoiceCloneSocketService {
    return this.injector.get(VoiceCloneSocketService);
  }

  private get voiceRecordingService(): VoiceRecordingService {
    return this.injector.get(VoiceRecordingService);
  }

  get processingState$() {
    return this.processingStateSubject.asObservable();
  }

  getProcessingState(): VoiceCloneProcessingState {
    return this.processingStateSubject.value;
  }

  async refreshVoiceCloneNarratorsFromServer(): Promise<void> {
    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      return;
    }
    await this.dexieNarratorsService.syncNarrators(jwtToken);
    await this.ensureVoiceCloneNarratorSlotMappings();
  }

  clearActiveVoiceClone(): void {
    this.activeVoiceCloneNarrator = undefined;
    this.processingStateSubject.next({ status: 'idle' });
  }

  async clearLocalVoiceCloneState(): Promise<void> {
    this.clearActiveVoiceClone();
    await this.voiceRecordingService.clearAllLocalVoiceCloneFiles();
    await this.setCreatedVoiceIds(new Set());
    await this.setVoiceCloneDisplayNames({});
    await this.setVoiceCloneAvatarUrls({});
    await this.setNarratorUuidToNamedSlotMap({});
    this.trialStateCache = null;
    this.optimisticOverrideDetected = false;
  }

  async getPaywallForStoryCreation(
    storyCount: number,
    options?: { forceEntitlementRefresh?: boolean }
  ): Promise<VoiceClonePaywallType> {
    void storyCount;
    if (
      options?.forceEntitlementRefresh ||
      !this.storeesSubscriptionService.isSubscribed
    ) {
      await this.storeesSubscriptionService.checkEntitlements({
        force: options?.forceEntitlementRefresh === true,
      });
    }

    // Qonversion (+ ad-reward week) is the client source of truth for paid access.
    if (this.storeesSubscriptionService.isSubscribed) {
      return null;
    }

    const state = await this.getVoiceCloneTrialState();
    if (state?.allowed) return null;
    if (!state) return 'subscription-required';

    if (state.reason === 'trial_narrations_exhausted' && state.trialActive) {
      return 'reverse-trial';
    }
    return 'bronze';
  }

  async getPaywallForVoiceCreation(
    storyCount?: number
  ): Promise<VoiceClonePaywallType> {
    void storyCount;
    await this.storeesSubscriptionService.checkEntitlements();
    if (this.storeesSubscriptionService.isSubscribed) return null;

    const state = await this.getVoiceCloneTrialState();
    if (!state) return 'subscription-required';

    // Backend trial path — respect opt-out before any trial window check.
    if (state.trialOptedOut) {
      return 'bronze';
    }

    // Voice clone creation is allowed if the trial window is active, even if narrations are exhausted.
    if (state.allowed || state.trialActive) {
      return null;
    }

    // Trial not started or expired -> bronze paywall
    if (state.reason === 'trial_not_started') return 'subscription-required';
    return 'bronze';
  }

  async getFreshPaywallForVoiceCreation(): Promise<VoiceClonePaywallType> {
    try {
      await this.storeesSubscriptionService.checkEntitlements({ force: true });
      if (this.storeesSubscriptionService.isSubscribed) {
        return null;
      }
      await this.refreshVoiceCloneTrialState({ force: true });
    } catch (error) {
      logger.warn('Voice clone creation access refresh failed', error);
    }

    return this.getPaywallForVoiceCreation();
  }

  async getFreshPaywallForStoryCreation(
    storyCount: number
  ): Promise<VoiceClonePaywallType> {
    try {
      await this.storeesSubscriptionService.checkEntitlements({ force: true });
      if (this.storeesSubscriptionService.isSubscribed) {
        return null;
      }
      await this.refreshVoiceCloneTrialState({ force: true });
    } catch (error) {
      logger.warn('Voice clone story creation access refresh failed', error);
    }

    return this.getPaywallForStoryCreation(storyCount);
  }

  async shouldShowReverseTrialPaywallAfterNarration(): Promise<boolean> {
    await this.storeesSubscriptionService.checkEntitlements();
    if (this.storeesSubscriptionService.isSubscribed) return false;
    const state = await this.getVoiceCloneTrialState();
    return !!state?.trialActive && state.trialOptedOut !== true;
  }

  async hasUserOptedOutOfTrial(): Promise<boolean> {
    // Paid store Bronze does not use the backend reverse-trial path.
    if (this.storeesSubscriptionService.hasPaidBronzeEntitlement) {
      return false;
    }
    const state = await this.getVoiceCloneTrialState({ force: true });
    return state?.trialOptedOut === true;
  }

  async markTrialAsDeclinedByUser(): Promise<void> {
    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      return;
    }
    const state = await this.storeesBackendService.optOutVoiceCloneTrial(jwtToken);
    this.trialStateCache = {
      value: state,
      fetchedAtMs: Date.now(),
      optimisticOverrideDetected: false,
    };
    this.optimisticOverrideDetected = false;
  }

  async startVoiceCloneTrial(): Promise<VoiceCloneTrialState | null> {
    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      return null;
    }
    const state = await this.storeesBackendService.startVoiceCloneTrial(jwtToken);
    this.trialStateCache = {
      value: state,
      fetchedAtMs: Date.now(),
      optimisticOverrideDetected: false,
    };
    this.optimisticOverrideDetected = false;
    return state;
  }

  /**
   * False when the user already has bronze-tier access (paid Qonversion or ad-reward week).
   * Use before presenting reverse-trial or bronze subscription UI.
   */
  async shouldPromptForVoiceCloneSubscription(): Promise<boolean> {
    await this.storeesSubscriptionService.checkEntitlements();
    return !this.storeesSubscriptionService.isSubscribed;
  }

  async getReverseTrialPaywallProps(): Promise<{
    currentDay: number;
    remainingDays: number;
    storiesRemaining: number;
  } | null> {
    await this.storeesSubscriptionService.checkEntitlements();
    if (this.storeesSubscriptionService.isSubscribed) {
      return null;
    }

    const state = await this.getVoiceCloneTrialState();
    if (
      !state?.trialActive ||
      state.trialOptedOut ||
      !state.trialDaysRemaining
    ) {
      return null;
    }

    const remainingDays = state.trialDaysRemaining;
    const currentDay = state.trialDaysTotal - remainingDays + 1;
    const storiesRemaining = Math.max(0, state.narrationsMax - state.narrationsUsed);
    return { currentDay, remainingDays, storiesRemaining };
  }

  async refreshVoiceCloneTrialState(options?: { force?: boolean }): Promise<void> {
    await this.getVoiceCloneTrialState(options);
  }

  public hadOptimisticOverride(): boolean {
    return this.optimisticOverrideDetected;
  }

  public hasPaidStoreSubscriptionEntitlement(): boolean {
    return this.storeesSubscriptionService.hasPaidBronzeEntitlement;
  }

  /** Narrations used in the voice-clone trial window (0 if not in trial / no session). */
  async getTrialNarrationsUsed(): Promise<number> {
    const state = await this.getVoiceCloneTrialState();
    return state?.narrationsUsed ?? 0;
  }

  async getVoiceCloneTrialState(
    options?: { force?: boolean }
  ): Promise<VoiceCloneTrialState | null> {
    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      this.trialStateCache = null;
      this.optimisticOverrideDetected = false;
      return null;
    }

    await this.storeesSubscriptionService.checkEntitlements({
      force: options?.force === true,
    });
    const localAllowedState = this.buildAllowedStateFromLocalSubscription();
    if (localAllowedState) {
      this.optimisticOverrideDetected = false;
      this.trialStateCache = {
        value: localAllowedState,
        fetchedAtMs: Date.now(),
        optimisticOverrideDetected: false,
      };
      return localAllowedState;
    }

    const cache = this.trialStateCache;
    const cacheTtlMs = 30_000;
    if (!options?.force && cache && Date.now() - cache.fetchedAtMs < cacheTtlMs) {
      this.optimisticOverrideDetected = cache.optimisticOverrideDetected ?? false;
      return cache.value;
    }

    this.optimisticOverrideDetected = false;
    try {
      const backendState = await this.storeesBackendService.checkVoiceCloneNarrationAccess(jwtToken);

      this.trialStateCache = {
        value: backendState,
        fetchedAtMs: Date.now(),
        optimisticOverrideDetected: false,
      };
      return backendState;
    } catch (error: unknown) {
      logger.warn('Voice clone narration access check failed', error);
      const fallbackAllowed = this.buildAllowedStateFromLocalSubscription();
      if (fallbackAllowed) {
        logger.warn(
          'VoiceCloneService: narration-access check failed but Qonversion entitlement is active; returning local allowed state',
          {
            assumedSource: fallbackAllowed.source,
          }
        );
        this.optimisticOverrideDetected = false;
        this.trialStateCache = {
          value: fallbackAllowed,
          fetchedAtMs: Date.now(),
          optimisticOverrideDetected: false,
        };
        return fallbackAllowed;
      }
      const message =
        error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
      const unauthorized =
        message.includes('unauthorized') || message.includes('401');
      if (unauthorized) {
        this.trialStateCache = null;
        this.optimisticOverrideDetected = false;
        return null;
      }
      if (cache && Date.now() - cache.fetchedAtMs < cacheTtlMs) {
        this.optimisticOverrideDetected = cache.optimisticOverrideDetected ?? false;
        return cache.value;
      }
      this.trialStateCache = null;
      this.optimisticOverrideDetected = false;
      return null;
    }
  }

  /**
   * Maps Qonversion/ad-reward client entitlement to narration-access shape.
   * Paid Bronze and ad-reward weeks do not need the backend trial endpoint.
   */
  private buildAllowedStateFromLocalSubscription(): VoiceCloneTrialState | null {
    if (!this.storeesSubscriptionService.isSubscribed) {
      return null;
    }

    return {
      allowed: true,
      source: this.storeesSubscriptionService.hasPaidBronzeEntitlement
        ? 'bronze'
        : 'weekly_free_premium',
      reason: undefined,
      trialStartedAt: null,
      trialOptedOut: false,
      narrationsUsed: 0,
      narrationsMax: 0,
      trialDaysTotal: 0,
      trialDaysRemaining: null,
      trialActive: false,
      weeklyFreePremiumExpiresAt: null,
    };
  }

  async createVoiceCloneRequest(input: {
    name: string;
    gender?: NarratorGender | null;
    avatarUrl?: string | null;
    requestMessage?: string | null;
  }): Promise<VoiceCloneRequest> {
    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      throw new Error('Missing session token');
    }

    return firstValueFrom(
      this.http.post<VoiceCloneRequest>(
        this.buildApiUrl(VOICE_CLONE_REQUESTS_ROUTE),
        {
          name: input.name.trim(),
          gender: input.gender ?? null,
          avatarUrl: input.avatarUrl ?? null,
          requestMessage: input.requestMessage?.trim() || null,
        },
        { headers: this.getHeaders(jwtToken, { json: true }) }
      )
    );
  }

  async getPublicVoiceCloneRequest(
    voiceCloneRequestId: string
  ): Promise<VoiceCloneRequest> {
    return firstValueFrom(
      this.http.get<VoiceCloneRequest>(
        this.buildApiUrl(
          'public',
          VOICE_CLONE_REQUESTS_ROUTE,
          voiceCloneRequestId
        )
      )
    );
  }

  async deleteVoiceCloneRequest(voiceCloneRequestId: string): Promise<void> {
    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      throw new Error('Missing session token');
    }

    await firstValueFrom(
      this.http.delete<void>(
        this.buildApiUrl(VOICE_CLONE_REQUESTS_ROUTE, voiceCloneRequestId),
        {
          headers: this.getHeaders(jwtToken, { json: true }),
          body: { dummy: 'placeholder' },
        }
      )
    );
    await this.purgeLocalVoiceCloneRequestArtifacts(voiceCloneRequestId);
  }

  /**
   * Deletes on-device request recordings and clears flow / in-memory recording when tied to this
   * request id (used after server delete and when dismissing a request locally).
   */
  async purgeLocalVoiceCloneRequestArtifacts(
    voiceCloneRequestId: string
  ): Promise<void> {
    const id = voiceCloneRequestId.trim();
    if (!id) {
      return;
    }

    await this.voiceRecordingService.deletePersistedRequestArtifacts(id);

    const ctx = await this.voiceCloneFlowService.getContext();
    if (ctx?.mode === 'request' && ctx.voiceCloneRequestId === id) {
      await this.voiceCloneFlowService.clear();
    }

    const data = this.voiceRecordingService.getRecordingData();
    if (data?.voiceCloneRequestId?.trim() === id) {
      await this.voiceRecordingService.clearAfterDiscard();
    }
  }

  async listVoiceCloneRequests(): Promise<VoiceCloneRequest[]> {
    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      return [];
    }

    return firstValueFrom(
      this.http.get<VoiceCloneRequest[]>(
        this.buildApiUrl(VOICE_CLONE_REQUESTS_ROUTE),
        { headers: this.getHeaders(jwtToken) }
      )
    );
  }

  getVoiceCloneRequestShareUrl(voiceCloneRequestId: string): string {
    const baseUrl = webAppBaseUrl ?? 'https://app.storees.ai';
    const trimmedBaseUrl = baseUrl.replace(/\/+$/, '');
    return `${trimmedBaseUrl}/voice-clone-web-welcome?voiceCloneRequestId=${encodeURIComponent(
      voiceCloneRequestId
    )}`;
  }

  async hasVoiceCreated(voiceId: string): Promise<boolean> {
    return !!(await this.getNarratorForVoiceId(voiceId));
  }

  async hasAnyVoiceCreated(): Promise<boolean> {
    const rows = await this.dexieNarratorsService.getVoiceCloneNarrators();
    return rows.some((n) =>
      VoiceCloneService.isFinalizedVoiceCloneForPicker(n)
    );
  }

  async getNarratorForVoiceId(voiceId: string): Promise<Narrator | undefined> {
    const voiceClones = (
      await this.dexieNarratorsService.getVoiceCloneNarrators()
    ).filter((n) => VoiceCloneService.isFinalizedVoiceCloneForPicker(n));

    if (voiceId === VOICE_CLONE_SLOT_MOM_ID || voiceId === VOICE_CLONE_SLOT_DAD_ID) {
      const slotName = VoiceCloneService.getVoiceSlotName(voiceId);
      return this.sortVoiceClonesByCreatedAt(voiceClones).find(
        (narrator) =>
          narrator.name?.trim().toLowerCase() === slotName
      );
    }

    if (isNamedCustomVoiceCloneId(voiceId)) {
      const narratorUuidFromSlot =
        tryParseNarratorUuidFromVoiceCloneCustomSlotId(voiceId);
      if (!narratorUuidFromSlot) {
        return undefined;
      }
      const byUuid =
        await this.dexieNarratorsService.getVoiceCloneNarratorById(
          narratorUuidFromSlot
        );
      if (
        byUuid &&
        VoiceCloneService.isFinalizedVoiceCloneForPicker(byUuid)
      ) {
        return byUuid;
      }
      return undefined;
    }

    return voiceClones.find((narrator) => narrator.id === voiceId);
  }

  async loadVoiceCloneNarrator(
    voiceCloneId: string
  ): Promise<Narrator | undefined> {
    if (this.activeVoiceCloneNarrator?.id === voiceCloneId) {
      return this.activeVoiceCloneNarrator;
    }

    let narrator =
      await this.dexieNarratorsService.getVoiceCloneNarratorById(voiceCloneId);
    if (!narrator) {
      const jwtToken = await this.authService.getSessionJwtToken();
      if (jwtToken) {
        await this.dexieNarratorsService.syncNarrators(jwtToken);
        narrator =
          await this.dexieNarratorsService.getVoiceCloneNarratorById(
            voiceCloneId
          );
      }
    }

    if (narrator) {
      this.activeVoiceCloneNarrator = narrator;
    }

    return narrator;
  }

  private async resolveVoiceCloneRequestIdForServiceCall(): Promise<string | null> {
    const recordingData = this.voiceRecordingService.getRecordingData();
    const flow = await this.voiceCloneFlowService.getContext();
    return VoiceCloneService.resolveVoiceCloneRequestIdForRecording(
      recordingData?.voiceType,
      flow,
      null,
      recordingData?.voiceCloneRequestId ?? null
    );
  }

  /**
   * Blocks {@link createVoiceCloneForRequest} when the public invite is finished, expired, or busy.
   * Purges local request artifacts when the server indicates the invite is gone or completed.
   */
  private async throwIfPublicVoiceCloneRequestCannotCreate(
    voiceCloneRequestId: string
  ): Promise<void> {
    const id = voiceCloneRequestId.trim();
    if (!id) {
      throw new VoiceCloneRequestNotUsableError(
        'This voice request could not be loaded.'
      );
    }

    const recordingData = this.voiceRecordingService.getRecordingData();
    const flow = await this.voiceCloneFlowService.getContext();
    logger.warn('[voice-clone] public voice clone create preflight', {
      voiceCloneRequestId: id,
      recordingRequestId: recordingData?.voiceCloneRequestId ?? null,
      flowRequestId:
        flow?.mode === 'request' ? flow.voiceCloneRequestId ?? null : null,
    });

    let request: VoiceCloneRequest;
    try {
      request = await this.getPublicVoiceCloneRequest(id);
    } catch (error) {
      const httpStatus =
        error instanceof HttpErrorResponse ? error.status : undefined;
      if (httpStatus === 404 || httpStatus === 410) {
        await this.purgeLocalVoiceCloneRequestArtifacts(id);
      }
      logger.warn('[voice-clone] public voice clone request preflight GET failed', {
        voiceCloneRequestId: id,
        httpStatus,
        message: this.getErrorMessage(error),
      });
      throw new VoiceCloneRequestNotUsableError(
        this.mapPublicVoiceClonePreflightFailureMessage(error)
      );
    }

    if (request.status === VoiceCloneRequestStatus.Completed) {
      await this.purgeLocalVoiceCloneRequestArtifacts(id);
      logger.warn('[voice-clone] public voice clone create blocked: request completed', {
        voiceCloneRequestId: id,
      });
      throw new VoiceCloneRequestNotUsableError(
        'This voice clone request has already been completed.'
      );
    }

    if (request.status === VoiceCloneRequestStatus.Cancelled) {
      await this.purgeLocalVoiceCloneRequestArtifacts(id);
      throw new VoiceCloneRequestNotUsableError(
        'This voice request is no longer available.'
      );
    }

    if (request.status === VoiceCloneRequestStatus.Expired) {
      await this.purgeLocalVoiceCloneRequestArtifacts(id);
      throw new VoiceCloneRequestNotUsableError(
        'This voice request has expired.'
      );
    }
  }

  private mapPublicVoiceClonePreflightFailureMessage(error: unknown): string {
    const raw = this.getErrorMessage(error);
    if (raw === 'Voice clone request is no longer available') {
      return 'This voice request is no longer available.';
    }
    if (raw === 'Voice clone request has expired') {
      return 'This voice request has expired.';
    }
    if (raw.toLowerCase().includes('not found')) {
      return 'This voice request could not be found.';
    }
    return raw;
  }

  async createVoiceCloneFromCurrentRecording(
    voiceName: string
  ): Promise<Narrator> {
    const recordingData = this.voiceRecordingService.getRecordingData();
    const payload = await this.voiceRecordingService.getRecordingAsBase64();

    if (
      !recordingData?.mimeType ||
      !recordingData.msDuration ||
      !payload?.base64
    ) {
      throw new Error('Recording data is missing');
    }

    const voiceCloneRequestId = await this.resolveVoiceCloneRequestIdForServiceCall();
    if (voiceCloneRequestId) {
      return this.createVoiceCloneForRequest(
        voiceCloneRequestId,
        voiceName,
        recordingData,
        payload
      );
    }

    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      throw new Error('Missing session token');
    }

    const requestMimeType = payload.mimeType || recordingData.mimeType;
    const fileExtension = this.getFileExtension(requestMimeType);
    const uploadMimeType = this.getMimeTypeForFileExtension(fileExtension);
    const durationMs = this.normalizeDurationMs(recordingData.msDuration);
    const requestBody: VoiceCloneCreateRequest = {
      name: voiceName,
      fileExtension,
      durationMs,
    };

    logger.info('Voice clone create request prepared', {
      voiceName,
      recordingMimeType: recordingData.mimeType,
      payloadMimeType: payload.mimeType,
      requestMimeType,
      uploadMimeType,
      fileExtension,
      durationMs,
    });

    this.processingStateSubject.next({
      status: 'creating',
      voiceName,
    });

    try {
      const createResponse = await firstValueFrom(
        this.http.post<VoiceCloneCreateResponse>(
          this.buildApiUrl(VOICE_CLONES_ROUTE),
          requestBody,
          { headers: this.getHeaders(jwtToken, { json: true }) }
        )
      );

      const voiceCloneId = createResponse.id;
      if (!voiceCloneId) {
        throw new Error('Voice clone creation did not return an ID');
      }
      if (!createResponse.uploadUrl) {
        throw new Error('Voice clone creation did not return an upload URL');
      }

      logger.info('Voice clone create response received', {
        voiceCloneId,
        uploadTarget: this.getUploadTarget(createResponse.uploadUrl),
        fileExtension,
        uploadMimeType,
      });

      this.voiceRecordingService.updateRecordingData({ voiceCloneId });
      this.processingStateSubject.next({
        status: 'uploading',
        voiceCloneId,
        voiceName,
      });

      await this.uploadReferenceAudio(
        createResponse.uploadUrl,
        payload.base64,
        uploadMimeType
      );

      const sampleReadyPromise = this.waitForVoiceCloneSample(
        jwtToken,
        voiceCloneId
      );
      await sampleReadyPromise.connected;

      this.processingStateSubject.next({
        status: 'processing',
        voiceCloneId,
        voiceName,
      });

      await firstValueFrom(
        this.http.patch<VoiceClone>(
          this.buildApiUrl(VOICE_CLONES_ROUTE, voiceCloneId, 'upload-complete'),
          {},
          { headers: this.getHeaders(jwtToken, { json: true }) }
        )
      );

      const sampleReady = await sampleReadyPromise;
      await this.dexieNarratorsService.syncNarrators(jwtToken);

      const narrator = await this.loadVoiceCloneNarrator(voiceCloneId);
      if (!narrator) {
        throw new Error('Voice clone was created but could not be loaded');
      }

      this.processingStateSubject.next({
        status: 'ready',
        voiceCloneId,
        voiceName: narrator.name ?? voiceName,
        narratorId: narrator.id,
        previewCloneAudioUrl:
          narrator.previewCloneAudioUrl ?? sampleReady.previewCloneAudioUrl,
      });

      return narrator;
    } catch (error) {
      const message = this.getErrorMessage(error);
      this.processingStateSubject.next({
        status: 'failed',
        voiceName,
        error: message,
      });
      throw this.normalizeVoiceCloneError(error, message);
    }
  }

  private async createVoiceCloneForRequest(
    voiceCloneRequestId: string,
    voiceName: string,
    recordingData: NonNullable<ReturnType<VoiceRecordingService['getRecordingData']>>,
    payload: { base64: string; mimeType: string }
  ): Promise<Narrator> {
    const requestMimeType = payload.mimeType || recordingData.mimeType;
    const fileExtension = this.getFileExtension(requestMimeType);
    const uploadMimeType = this.getMimeTypeForFileExtension(fileExtension);
    const durationMs = this.normalizeDurationMs(recordingData.msDuration);

    this.processingStateSubject.next({
      status: 'creating',
      voiceName,
    });

    try {
      await this.throwIfPublicVoiceCloneRequestCannotCreate(voiceCloneRequestId);

      const createResponse = await firstValueFrom(
        this.http.post<
          Omit<VoiceCloneCreateResponse, 'referenceAudioUrl' | 'previewCloneAudioUrl' | 'userId'> & {
            id: string;
            uploadUrl: string;
          }
        >(
          this.buildApiUrl(
            'public',
            VOICE_CLONE_REQUESTS_ROUTE,
            voiceCloneRequestId.trim(),
            'voice-clones'
          ),
          {
            fileExtension,
            durationMs,
            gender: recordingData.narratorGender,
          },
          { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
        )
      );

      const voiceCloneId = createResponse.id;
      if (!voiceCloneId || !createResponse.uploadUrl) {
        throw new Error('Voice clone creation did not return upload details');
      }

      this.voiceRecordingService.updateRecordingData({
        voiceCloneId,
      });
      this.processingStateSubject.next({
        status: 'uploading',
        voiceCloneId,
        voiceName,
      });

      await this.uploadReferenceAudio(
        createResponse.uploadUrl,
        payload.base64,
        uploadMimeType
      );

      this.processingStateSubject.next({
        status: 'processing',
        voiceCloneId,
        voiceName,
      });

      await firstValueFrom(
        this.http.patch(
          this.buildApiUrl(
            'public',
            VOICE_CLONE_REQUESTS_ROUTE,
            voiceCloneRequestId,
            'voice-clones',
            voiceCloneId,
            'upload-complete'
          ),
          {},
          { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
        )
      );

      const publicRequest = await this.waitForPublicVoiceCloneSample(
        voiceCloneRequestId,
        voiceCloneId
      );
      this.processingStateSubject.next({
        status: 'ready',
        voiceCloneId,
        voiceName,
        narratorId: voiceCloneId,
        previewCloneAudioUrl: publicRequest.previewDownloadUrl ?? null,
      });
      await this.voiceCloneFlowService.update({
        voiceCloneId,
        requestStatus: publicRequest.status,
      });

      return {
        id: voiceCloneId,
        name: publicRequest.requestedName || voiceName,
        voice: NarratorVoice.voice_clone,
        previewCloneAudioUrl: publicRequest.previewDownloadUrl ?? null,
        createdAt: publicRequest.createdAt,
      } as Narrator;
    } catch (error) {
      if (error instanceof VoiceCloneRequestNotUsableError) {
        this.processingStateSubject.next({
          status: 'failed',
          voiceName,
          error: error.message,
        });
        throw error;
      }

      let message = this.getErrorMessage(error);
      const httpStatus =
        error instanceof HttpErrorResponse ? error.status : undefined;

      if (
        httpStatus === 409 &&
        message.includes('already been completed')
      ) {
        await this.purgeLocalVoiceCloneRequestArtifacts(voiceCloneRequestId);
        message =
          'This voice clone request has already been completed.';
        logger.warn('[voice-clone] public voice clone POST blocked: already completed', {
          voiceCloneRequestId,
        });
        this.processingStateSubject.next({
          status: 'failed',
          voiceName,
          error: message,
        });
        throw new VoiceCloneRequestNotUsableError(message);
      }

      this.processingStateSubject.next({
        status: 'failed',
        voiceName,
        error: message,
      });
      throw this.normalizeVoiceCloneError(error, message);
    }
  }

  async syncSignedInTrialState(): Promise<void> {
    await this.refreshVoiceCloneTrialState({ force: true });
  }

  async deleteVoiceClone(voiceCloneId: string): Promise<void> {
    logger.info('[voice-clone] deleteVoiceClone requested', { voiceCloneId });
    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      logger.error('[voice-clone] deleteVoiceClone aborted: missing session token', {
        voiceCloneId,
      });
      throw new Error('Missing session token');
    }

    try {
      await firstValueFrom(
        this.http.delete<void>(this.buildApiUrl(VOICE_CLONES_ROUTE, voiceCloneId), {
          headers: this.getHeaders(jwtToken),
        })
      );
    } catch (err) {
      logger.error('[voice-clone] deleteVoiceClone API failed', {
        voiceCloneId,
        err,
      });
      throw err;
    }

    logger.info('[voice-clone] deleteVoiceClone API succeeded; syncing narrators', {
      voiceCloneId,
    });

    await this.dexieNarratorsService.syncNarrators(jwtToken);
    try {
      const { DexieProfilesService } = await import('./dexie/dexie-profiles.service');
      await this.injector.get(DexieProfilesService).syncProfiles(jwtToken);
    } catch (err) {
      logger.warn(
        '[voice-clone] Failed to sync profiles after deleting voice clone',
        err
      );
    }
    if (this.activeVoiceCloneNarrator?.id === voiceCloneId) {
      this.clearActiveVoiceClone();
    }
  }

  /**
   * Deletes a draft voice clone when the user leaves modify before Save (no finalizedAt).
   */
  async abandonUnfinalizedVoiceClone(
    voiceCloneId: string | null | undefined
  ): Promise<void> {
    const id = voiceCloneId?.trim();
    if (!id) {
      return;
    }
    let row = await this.dexieNarratorsService.getVoiceCloneNarratorById(id);
    if (!row) {
      const jwtToken = await this.authService.getSessionJwtToken();
      if (jwtToken) {
        await this.dexieNarratorsService.syncNarrators(jwtToken);
        row = await this.dexieNarratorsService.getVoiceCloneNarratorById(id);
      }
    }
    if (!row || VoiceCloneService.isFinalizedVoiceCloneForPicker(row)) {
      return;
    }
    try {
      await this.deleteVoiceClone(id);
    } catch (err) {
      logger.warn('[voice-clone] abandonUnfinalizedVoiceClone failed', {
        voiceCloneId: id,
        err,
      });
    }
  }

  async finalizeVoiceClone(voiceCloneId: string): Promise<Narrator> {
    const voiceCloneRequestId = await this.resolveVoiceCloneRequestIdForServiceCall();
    if (voiceCloneRequestId) {
      const narrator = await firstValueFrom(
        this.http.patch<Narrator>(
          this.buildApiUrl(
            'public',
            VOICE_CLONE_REQUESTS_ROUTE,
            voiceCloneRequestId,
            'voice-clones',
            voiceCloneId,
            'finalize'
          ),
          {},
          { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
        )
      );

      const jwtToken = await this.authService.getSessionJwtToken();
      let syncedNarrator = narrator as Narrator;
      if (jwtToken) {
        await this.dexieNarratorsService.syncNarrators(jwtToken);
        syncedNarrator =
          (await this.loadVoiceCloneNarrator(voiceCloneId)) ??
          (narrator as Narrator);
      }

      const flow = await this.voiceCloneFlowService.getContext();
      const recordingData = this.voiceRecordingService.getRecordingData();
      const displayName =
        syncedNarrator.name?.trim() ||
        flow?.requestedName?.trim() ||
        recordingData?.voiceName?.trim() ||
        '';
      const nid = syncedNarrator.id?.trim();
      if (nid) {
        const uuidSlot = voiceCloneCustomNarratorIdFromNarratorUuid(nid);
        await this.markVoiceCreated(uuidSlot);
        if (displayName) {
          await this.persistVoiceCloneDisplayName(uuidSlot, displayName);
        }
        await this.persistNarratorUuidToNamedSlot(nid, uuidSlot);
        const avatarTrim =
          flow?.requestedAvatarUrl?.trim() ||
          recordingData?.avatarUrl?.trim();
        if (avatarTrim) {
          await this.persistVoiceCloneSlotAvatar(uuidSlot, nid, avatarTrim);
        }
        this.voiceRecordingService.updateRecordingData({
          customNarratorId: uuidSlot,
        });
      }

      await this.ensureVoiceCloneNarratorSlotMappings();

      this.activeVoiceCloneNarrator = syncedNarrator;

      await this.voiceCloneFlowService.clear();
      await this.voiceRecordingService.clearAfterDiscard();

      return syncedNarrator;
    }

    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      throw new Error('Missing session token');
    }

    const narrator = await firstValueFrom(
      this.http.patch<VoiceClone>(
        this.buildApiUrl(VOICE_CLONES_ROUTE, voiceCloneId, 'finalize'),
        {},
        { headers: this.getHeaders(jwtToken, { json: true }) }
      )
    );

    await this.dexieNarratorsService.syncNarrators(jwtToken);
    const syncedNarrator =
      (await this.loadVoiceCloneNarrator(voiceCloneId)) ??
      (narrator as unknown as Narrator);
    this.activeVoiceCloneNarrator = syncedNarrator;

    const rec = this.voiceRecordingService.getRecordingData();
    const slotFromRecording = this.getCurrentSlotId();
    const narratorUuid = syncedNarrator.id?.trim();

    if (
      slotFromRecording === VOICE_CLONE_SLOT_MOM_ID ||
      slotFromRecording === VOICE_CLONE_SLOT_DAD_ID
    ) {
      await this.markVoiceCreated(slotFromRecording);
    } else if (narratorUuid) {
      const uuidSlot = voiceCloneCustomNarratorIdFromNarratorUuid(narratorUuid);
      await this.markVoiceCreated(uuidSlot);
      const displayName =
        rec?.voiceName?.trim() || syncedNarrator.name?.trim() || '';
      if (displayName) {
        await this.persistVoiceCloneDisplayName(uuidSlot, displayName);
      }
      const avatarTrim = rec?.avatarUrl?.trim();
      if (avatarTrim) {
        await this.persistVoiceCloneSlotAvatar(uuidSlot, narratorUuid, avatarTrim);
      }
      await this.persistNarratorUuidToNamedSlot(narratorUuid, uuidSlot);
      this.voiceRecordingService.updateRecordingData({
        customNarratorId: uuidSlot,
      });
    }

    return syncedNarrator;
  }

  /**
   * PATCH display name for a finalized signed-in custom voice clone (not Mom/Dad; not public request flow).
   */
  async renameFinalizedVoiceClone(
    voiceCloneId: string,
    newDisplayName: string,
    namedCustomSlotId: string | null | undefined
  ): Promise<Narrator> {
    const trimmed = newDisplayName.trim();
    if (!trimmed) {
      throw new Error('Voice name is required');
    }

    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      throw new Error('Missing session token');
    }

    const narrator = await firstValueFrom(
      this.http.patch<Narrator>(
        this.buildApiUrl(VOICE_CLONES_ROUTE, voiceCloneId.trim()),
        { name: trimmed },
        { headers: this.getHeaders(jwtToken, { json: true }) }
      )
    );

    await this.dexieNarratorsService.syncNarrators(jwtToken);

    await this.ensureVoiceCloneNarratorSlotMappings();

    void namedCustomSlotId;
    const narratorUuid = narrator.id?.trim();
    if (narratorUuid) {
      const slotId = voiceCloneCustomNarratorIdFromNarratorUuid(narratorUuid);
      await this.persistVoiceCloneDisplayName(slotId, trimmed);
      await this.persistNarratorUuidToNamedSlot(narratorUuid, slotId);
    }

    this.voiceRecordingService.updateRecordingData({ voiceName: trimmed });

    const synced =
      (await this.loadVoiceCloneNarrator(voiceCloneId.trim())) ??
      (narrator as Narrator);
    this.activeVoiceCloneNarrator = synced;

    return synced;
  }

  async getVoiceCloneAssetDownloadUrl(
    voiceCloneId: string,
    asset: VoiceCloneDownloadAsset
  ): Promise<string | null> {
    const voiceCloneRequestId = await this.resolveVoiceCloneRequestIdForServiceCall();
    if (voiceCloneRequestId && asset === 'previewCloneAudioUrl') {
      try {
        const publicRequest =
          await this.getPublicVoiceCloneRequest(voiceCloneRequestId);
        const linkedId = publicRequest.voiceCloneId?.trim() || '';
        const narrId = voiceCloneId.trim();
        if (linkedId && linkedId === narrId) {
          const preview = publicRequest.previewDownloadUrl ?? null;
          if (preview) {
            return preview;
          }
        }
      } catch {
        // Fall through: signed-in user can still use JWT download for other clones.
      }
    }

    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      throw new Error('Missing session token');
    }

    try {
      const response = await firstValueFrom(
        this.http.get<{ downloadUrl: string }>(
          this.buildApiUrl(VOICE_CLONES_ROUTE, voiceCloneId, 'download-url'),
          {
            headers: this.getHeaders(jwtToken),
            params: { asset },
          }
        )
      );

      if (!response.downloadUrl) {
        return null;
      }
      return response.downloadUrl;
    } catch (error) {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404) {
        return null;
      }
      throw error;
    }
  }

  async markVoiceCreated(voiceId: string): Promise<void> {
    const ids = await this.getCreatedVoiceIds();
    ids.add(voiceId);
    await this.setCreatedVoiceIds(ids);
  }

  async persistVoiceCloneSlotAvatar(
    namedSlotId: string,
    narratorUuid: string | null | undefined,
    avatarUrl: string | null | undefined
  ): Promise<void> {
    const trimmed = avatarUrl?.trim();
    const slot = namedSlotId?.trim();
    if (!trimmed || !slot || !isNamedCustomVoiceCloneId(slot)) {
      return;
    }
    const map = await this.getVoiceCloneAvatarUrls();
    map[slot] = trimmed;
    const nid = narratorUuid?.trim();
    if (nid) {
      map[nid] = trimmed;
    }
    await this.setVoiceCloneAvatarUrls(map);
  }

  async resolveSessionAvatarUrlForCustomSlot(
    namedSlotId: string | null | undefined,
    narratorUuid: string | null | undefined
  ): Promise<string | null> {
    const slot = namedSlotId?.trim();
    const nid = narratorUuid?.trim();
    if (!slot && !nid) {
      return null;
    }
    const map = await this.getVoiceCloneAvatarUrls();
    const fromSlot = slot ? map[slot]?.trim() : '';
    if (fromSlot) {
      return fromSlot;
    }
    const fromNid = nid ? map[nid]?.trim() : '';
    return fromNid || null;
  }

  async deleteLocalVoiceClone(
    voiceName: string,
    voiceType: 'custom' | 'mom' | 'dad',
    narratorId?: string | null,
    voiceCloneUuid?: string | null
  ): Promise<void> {
    void voiceName;
    let slotId: string | null = null;
    if (voiceType === 'mom') slotId = VOICE_CLONE_SLOT_MOM_ID;
    else if (voiceType === 'dad') slotId = VOICE_CLONE_SLOT_DAD_ID;
    else if (voiceCloneUuid?.trim()) {
      slotId = voiceCloneCustomNarratorIdFromNarratorUuid(voiceCloneUuid.trim());
    } else if (narratorId?.trim()) {
      slotId = narratorId.trim();
    }

    if (slotId) {
      const ids = await this.getCreatedVoiceIds();
      ids.delete(slotId);
      await this.setCreatedVoiceIds(ids);
      await this.removeVoiceCloneDisplayName(slotId);
      await this.removeVoiceCloneAvatarUrlsFor([slotId, voiceCloneUuid]);
      await this.purgeNamedSlotFromNarratorUuidMap(slotId);
      await this.dexieNarratorsService.removeVoiceCloneNarratorById(slotId);
    }
    if (voiceCloneUuid?.trim()) {
      await this.removeNarratorUuidFromNamedSlotMap(voiceCloneUuid.trim());
    }
  }

  async listNamedCustomVoiceSlotsForPicker(): Promise<
    Array<{
      id: string;
      name: string;
      gender?: NarratorGender;
      avatarUrl: string;
      narratorId?: string;
      previewCloneAudioUrl?: string | null;
    }>
  > {
    await this.ensureVoiceCloneNarratorSlotMappings();
    const ids = await this.getCreatedVoiceIds();
    const sessionSlotsForPicker = await this.getCreatedVoiceIdsFromSession();
    const displayNames = await this.getVoiceCloneDisplayNames();
    const avatarOverrides = await this.getVoiceCloneAvatarUrls();
    const voiceClones = await this.dexieNarratorsService.getVoiceCloneNarrators();

    const rows: Array<{
      id: string;
      name: string;
      gender?: NarratorGender;
      avatarUrl: string;
      narratorId?: string;
      previewCloneAudioUrl?: string | null;
    }> = [];

    for (const id of ids) {
      if (!isNamedCustomVoiceCloneId(id)) {
        continue;
      }

      const narratorUuidFromSlot =
        tryParseNarratorUuidFromVoiceCloneCustomSlotId(id);
      if (!narratorUuidFromSlot) {
        continue;
      }

      const narrator = this.sortVoiceClonesByCreatedAt(voiceClones).find(
        (item) =>
          VoiceCloneService.isFinalizedVoiceCloneForPicker(item) &&
          item.id?.trim().toLowerCase() === narratorUuidFromSlot
      );
      if (!narrator) {
        continue;
      }

      const displayName = displayNames[id]?.trim();
      const name = displayName || narrator.name?.trim() || '';
      if (!name) {
        continue;
      }

      const slotOverride = avatarOverrides[id]?.trim();
      const narratorOverride = narrator?.id
        ? avatarOverrides[narrator.id]?.trim()
        : undefined;
      const sessionAvatar = slotOverride || narratorOverride || null;

      const avatarUrl =
        (sessionAvatar ||
          resolveVoiceCloneNarratorAvatarUrl({
            id,
            gender: narrator?.gender,
            avatarImageUrl:
              (narrator as { avatarImageUrl?: string } | undefined)?.avatarImageUrl,
          }) ||
          stableVoiceCloneAvatarUrlForNarrator(
            id,
            narrator?.gender ?? NarratorGender.Male
          )) as string;

      rows.push({
        id,
        name,
        gender: narrator?.gender,
        avatarUrl,
        narratorId: narrator?.id,
        previewCloneAudioUrl: narrator?.previewCloneAudioUrl ?? null,
      });
    }

    const deduped = this.pickUniqueNamedCustomVoicePickerRows(
      rows,
      sessionSlotsForPicker
    );

    return deduped.sort((left, right) =>
      left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
    );
  }

  /** One row per narrator UUID (session vs Dexie tie-break only when ids collide). */
  private pickUniqueNamedCustomVoicePickerRows(
    rows: Array<{
      id: string;
      name: string;
      gender?: NarratorGender;
      avatarUrl: string;
      narratorId?: string;
      previewCloneAudioUrl?: string | null;
    }>,
    sessionSlotIds: Set<string>
  ): typeof rows {
    const byKey = new Map<string, (typeof rows)[number]>();

    const preferCandidate = (
      existing: (typeof rows)[number],
      candidate: (typeof rows)[number]
    ): (typeof rows)[number] => {
      const existingSession = sessionSlotIds.has(existing.id) ? 1 : 0;
      const candidateSession = sessionSlotIds.has(candidate.id) ? 1 : 0;
      if (candidateSession !== existingSession) {
        return candidateSession > existingSession ? candidate : existing;
      }
      return candidate.id.localeCompare(existing.id, undefined, {
        sensitivity: 'base',
      }) < 0
        ? candidate
        : existing;
    };

    for (const row of rows) {
      const nk = row.narratorId?.trim() ? row.narratorId.trim() : `slot:${row.id}`;
      const existing = byKey.get(nk);
      byKey.set(nk, existing ? preferCandidate(existing, row) : row);
    }

    return [...byKey.values()];
  }

  async getNamedCustomVoiceLabelMap(): Promise<Record<string, string>> {
    const rows = await this.listNamedCustomVoiceSlotsForPicker();
    return Object.fromEntries(rows.map((row) => [row.id, row.name]));
  }

  async mergeVoiceClonePlaceholdersIntoNarrators(
    narrators: Narrator[]
  ): Promise<Narrator[]> {
    return narrators;
  }

  static getVoiceIdForSlot(voiceType: 'custom' | 'mom' | 'dad'): string | null {
    if (voiceType === 'mom') return VOICE_CLONE_SLOT_MOM_ID;
    if (voiceType === 'dad') return VOICE_CLONE_SLOT_DAD_ID;
    return null;
  }

  static getSlotTypeFromVoiceId(
    voiceId: string
  ): 'mom' | 'dad' | 'custom' | null {
    if (voiceId === VOICE_CLONE_SLOT_MOM_ID) return 'mom';
    if (voiceId === VOICE_CLONE_SLOT_DAD_ID) return 'dad';
    if (isNamedCustomVoiceCloneId(voiceId)) return 'custom';
    return null;
  }

  /** Mom/Dad are always the signed-in user's on-device narrators — never public request clones. */
  static isMomOrDadVoiceType(voiceType: string | null | undefined): boolean {
    return voiceType === 'mom' || voiceType === 'dad';
  }

  /**
   * Whether a voice-clone-request id applies to this recording. Mom/Dad and standard-flow custom
   * clones use signed-in APIs only; stale request/session state must not send local custom clips
   * through public request endpoints.
   *
   * When `allowPersistedRequestFlowId` is false (save / route hydration), the persisted flow’s
   * request id is ignored unless the URL (`routeVoiceCloneRequestId`) or the active recording
   * (`recordingVoiceCloneRequestId`) references it. This prevents a stale in-session request flow
   * from looking like “request mode” for a new local recording. Record / service callers keep the
   * default (true) so in-app request flows still resolve before the user has re-written recording
   * metadata.
   */
  static resolveVoiceCloneRequestIdForRecording(
    voiceType: 'custom' | 'mom' | 'dad' | undefined,
    flow: VoiceCloneFlowContext | null | undefined,
    routeVoiceCloneRequestId: string | null | undefined,
    recordingVoiceCloneRequestId: string | null | undefined,
    options?: { allowPersistedRequestFlowId?: boolean }
  ): string | null {
    const allowPersisted = options?.allowPersistedRequestFlowId !== false;

    if (VoiceCloneService.isMomOrDadVoiceType(voiceType)) {
      return null;
    }
    const route = routeVoiceCloneRequestId?.trim() || null;
    const rec = recordingVoiceCloneRequestId?.trim() || null;
    const flowReq =
      flow?.mode === 'request' ? flow.voiceCloneRequestId?.trim() || null : null;

    if (route) {
      return route;
    }

    // Active recording session wins over flow mode (e.g. My Voices called startStandardFlow
    // but the user is still finalizing a request-originated recording).
    if (rec) {
      return rec;
    }

    if (flow?.mode === 'standard') {
      return null;
    }

    if (flowReq) {
      if (!allowPersisted && !route && !rec) {
        return null;
      }
      return flowReq;
    }

    return null;
  }

  /** User-owed voice clones count for My Voices / pickers only after finalize (modify Save). */
  static isFinalizedVoiceCloneForPicker(narrator: Narrator): boolean {
    return (
      narrator.finalizedAt != null &&
      String(narrator.finalizedAt).trim() !== ''
    );
  }

  static isVoiceCloneNarrator(narratorVoiceId: string): boolean {
    return (
      typeof narratorVoiceId === 'string' &&
      narratorVoiceId.startsWith('voice-clone-')
    );
  }

  private async isResolvedVoiceCloneNarrator(
    narratorVoiceId: string
  ): Promise<boolean> {
    if (VoiceCloneService.isVoiceCloneNarrator(narratorVoiceId)) {
      return true;
    }

    const narrator = await this.getNarratorForVoiceId(narratorVoiceId);
    return narrator?.voice === NarratorVoice.voice_clone;
  }

  private async getCreatedVoiceIds(): Promise<Set<string>> {
    const fromSession = await this.getCreatedVoiceIdsFromSession();
    const fromDexie = await this.getCreatedVoiceIdsFromDexie();
    return new Set([...fromDexie, ...fromSession]);
  }

  private async getCreatedVoiceIdsFromSession(): Promise<Set<string>> {
    const stored = await this.sessionService.getPersistent(VOICE_CLONE_CREATED_KEYS);
    if (!stored) return new Set();
    try {
      const parsed = JSON.parse(stored) as string[];
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  }

  /** Slots implied by synced Dexie narrators; session keys remain separate in `getCreatedVoiceIds`. */
  private async getCreatedVoiceIdsFromDexie(): Promise<Set<string>> {
    const ids = new Set<string>();
    const narrators = await this.dexieNarratorsService.getVoiceCloneNarrators();

    for (const narrator of narrators) {
      if (!VoiceCloneService.isFinalizedVoiceCloneForPicker(narrator)) {
        continue;
      }
      const trimmedName = narrator.name?.trim();
      const normalizedName = trimmedName?.toLowerCase();
      if (normalizedName === 'mom') {
        ids.add(VOICE_CLONE_SLOT_MOM_ID);
      } else if (normalizedName === 'dad') {
        ids.add(VOICE_CLONE_SLOT_DAD_ID);
      } else if (trimmedName && normalizedName) {
        const nid = narrator.id?.trim();
        if (!nid) {
          continue;
        }
        ids.add(voiceCloneCustomNarratorIdFromNarratorUuid(nid));
      }
    }
    return ids;
  }

  private async setCreatedVoiceIds(ids: Set<string>): Promise<void> {
    await this.sessionService.setPersistent(
      VOICE_CLONE_CREATED_KEYS,
      JSON.stringify(Array.from(ids))
    );
  }

  private async getVoiceCloneDisplayNames(): Promise<Record<string, string>> {
    const stored = await this.sessionService.getPersistent(
      VOICE_CLONE_DISPLAY_NAMES_KEY
    );
    if (!stored) {
      return {};
    }
    try {
      const parsed = JSON.parse(stored) as Record<string, string>;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }

  private async setVoiceCloneDisplayNames(
    map: Record<string, string>
  ): Promise<void> {
    await this.sessionService.setPersistent(
      VOICE_CLONE_DISPLAY_NAMES_KEY,
      JSON.stringify(map)
    );
  }

  private async persistVoiceCloneDisplayName(
    voiceId: string,
    displayName: string
  ): Promise<void> {
    const trimmed = displayName.trim();
    if (!trimmed || !isNamedCustomVoiceCloneId(voiceId)) {
      return;
    }
    const map = await this.getVoiceCloneDisplayNames();
    map[voiceId] = trimmed;
    await this.setVoiceCloneDisplayNames(map);
  }

  private async removeVoiceCloneDisplayName(voiceId: string): Promise<void> {
    const map = await this.getVoiceCloneDisplayNames();
    if (!map[voiceId]) return;
    delete map[voiceId];
    await this.setVoiceCloneDisplayNames(map);
  }

  private async getNarratorUuidToNamedSlotMap(): Promise<Record<string, string>> {
    const stored = await this.sessionService.getPersistent(
      VOICE_CLONE_NARRATOR_NAMED_SLOT_KEY
    );
    if (!stored) {
      return {};
    }
    try {
      const parsed = JSON.parse(stored) as Record<string, string>;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }

  private async setNarratorUuidToNamedSlotMap(
    map: Record<string, string>
  ): Promise<void> {
    await this.sessionService.setPersistent(
      VOICE_CLONE_NARRATOR_NAMED_SLOT_KEY,
      JSON.stringify(map)
    );
  }

  private async persistNarratorUuidToNamedSlot(
    narratorUuid: string,
    namedSlotId: string
  ): Promise<void> {
    const uuid = narratorUuid.trim();
    const sid = namedSlotId.trim();
    if (!uuid || !isNamedCustomVoiceCloneId(sid)) {
      return;
    }
    const map = await this.getNarratorUuidToNamedSlotMap();
    map[uuid] = sid;
    await this.setNarratorUuidToNamedSlotMap(map);
  }

  private async removeNarratorUuidFromNamedSlotMap(
    narratorUuid: string | null | undefined
  ): Promise<void> {
    const uuid = narratorUuid?.trim();
    if (!uuid) {
      return;
    }
    const map = await this.getNarratorUuidToNamedSlotMap();
    if (!(uuid in map)) {
      return;
    }
    delete map[uuid];
    await this.setNarratorUuidToNamedSlotMap(map);
  }

  private async purgeNamedSlotFromNarratorUuidMap(
    namedSlotId: string | null | undefined
  ): Promise<void> {
    const sid = namedSlotId?.trim();
    if (!sid) {
      return;
    }
    const map = await this.getNarratorUuidToNamedSlotMap();
    let changed = false;
    for (const [uuid, mapped] of Object.entries(map)) {
      if (mapped === sid) {
        delete map[uuid];
        changed = true;
      }
    }
    if (changed) {
      await this.setNarratorUuidToNamedSlotMap(map);
    }
  }

  /**
   * Keeps narrator UUID → local slot id aligned with `voice-clone-custom-<uuid>` (finalize writes this too).
   */
  private async ensureVoiceCloneNarratorSlotMappings(): Promise<void> {
    const map = await this.getNarratorUuidToNamedSlotMap();
    const narrators = await this.dexieNarratorsService.getVoiceCloneNarrators();
    const next = { ...map };
    let changed = false;

    for (const narrator of narrators) {
      if (!VoiceCloneService.isFinalizedVoiceCloneForPicker(narrator)) {
        continue;
      }
      const trimmed = narrator.name?.trim();
      const low = trimmed?.toLowerCase();
      if (!trimmed || !low || !narrator.id?.trim()) {
        continue;
      }
      if (low === 'mom' || low === 'dad') {
        continue;
      }

      const nid = narrator.id.trim();
      const uuidSlot = voiceCloneCustomNarratorIdFromNarratorUuid(nid);
      const existing = next[nid]?.trim();
      if (existing !== uuidSlot) {
        next[nid] = uuidSlot;
        changed = true;
      }
    }

    if (changed) {
      await this.setNarratorUuidToNamedSlotMap(next);
    }
  }

  private async getVoiceCloneAvatarUrls(): Promise<Record<string, string>> {
    const stored = await this.sessionService.getPersistent(
      VOICE_CLONE_AVATAR_URLS_KEY
    );
    if (!stored) {
      return {};
    }
    try {
      const parsed = JSON.parse(stored) as Record<string, string>;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }

  private async setVoiceCloneAvatarUrls(map: Record<string, string>): Promise<void> {
    await this.sessionService.setPersistent(
      VOICE_CLONE_AVATAR_URLS_KEY,
      JSON.stringify(map)
    );
  }

  private async removeVoiceCloneAvatarUrlsFor(
    identifiers: Array<string | null | undefined>
  ): Promise<void> {
    const map = await this.getVoiceCloneAvatarUrls();
    let changed = false;
    for (const raw of identifiers) {
      const key = raw?.trim();
      if (key && key in map) {
        delete map[key];
        changed = true;
      }
    }
    if (changed) {
      await this.setVoiceCloneAvatarUrls(map);
    }
  }

  private getCurrentSlotId(): string | null {
    const recordingData = this.voiceRecordingService.getRecordingData();
    if (!recordingData) return null;
    if (recordingData.voiceType === 'mom') return VOICE_CLONE_SLOT_MOM_ID;
    if (recordingData.voiceType === 'dad') return VOICE_CLONE_SLOT_DAD_ID;
    return recordingData.customNarratorId?.trim() || null;
  }

  private static getVoiceSlotName(voiceId: string): string | null {
    if (voiceId === VOICE_CLONE_SLOT_MOM_ID) return 'mom';
    if (voiceId === VOICE_CLONE_SLOT_DAD_ID) return 'dad';
    return null;
  }

  private getHeaders(
    jwtToken: string,
    options: { json?: boolean } = {}
  ): HttpHeaders {
    let headers = new HttpHeaders({
      Authorization: `Bearer ${jwtToken}`,
    });

    if (options.json) {
      headers = headers.set('Content-Type', 'application/json');
    }

    return headers;
  }

  private buildApiUrl(...segments: Array<string | undefined>): string {
    return urlJoin(
      apiBaseUrl || '',
      ...segments.filter((segment): segment is string => !!segment)
    );
  }

  private sortVoiceClonesByCreatedAt(narrators: Narrator[]): Narrator[] {
    return [...narrators].sort((left, right) => {
      const leftTime = new Date(left.createdAt ?? 0).getTime();
      const rightTime = new Date(right.createdAt ?? 0).getTime();
      return rightTime - leftTime;
    });
  }

  private getFileExtension(
    mimeType: string
  ): VoiceCloneCreateRequestFileExtension {
    const normalizedType = mimeType.toLowerCase();

    if (
      normalizedType.includes('webm') ||
      normalizedType.endsWith('.webm')
    ) {
      return VoiceCloneCreateRequestFileExtension.webm;
    }

    if (
      normalizedType.includes('mp4') ||
      normalizedType.includes('m4a') ||
      normalizedType.endsWith('.m4a')
    ) {
      return VoiceCloneCreateRequestFileExtension.m4a;
    }

    if (normalizedType.includes('aac') || normalizedType.endsWith('.aac')) {
      return VoiceCloneCreateRequestFileExtension.aac;
    }

    if (
      normalizedType.includes('mpeg') ||
      normalizedType.includes('mp3') ||
      normalizedType.endsWith('.mp3')
    ) {
      return VoiceCloneCreateRequestFileExtension.mp3;
    }

    return VoiceCloneCreateRequestFileExtension.wav;
  }

  private getMimeTypeForFileExtension(
    fileExtension: VoiceCloneCreateRequestFileExtension
  ): string {
    switch (fileExtension) {
      case VoiceCloneCreateRequestFileExtension.webm:
        return 'audio/webm';
      case VoiceCloneCreateRequestFileExtension.m4a:
        return 'audio/mp4';
      case VoiceCloneCreateRequestFileExtension.aac:
        return 'audio/aac';
      case VoiceCloneCreateRequestFileExtension.mp3:
        return 'audio/mpeg';
      default:
        return 'audio/wav';
    }
  }

  private normalizeDurationMs(rawDurationMs: unknown): number {
    const durationMs = Number(rawDurationMs);
    if (!Number.isFinite(durationMs) || durationMs <= 0) {
      throw new Error('Recording data is missing');
    }

    return Math.max(1, Math.round(durationMs));
  }

  private async uploadReferenceAudio(
    uploadUrl: string,
    base64Data: string,
    mimeType: string
  ): Promise<void> {
    logger.info('Voice clone reference upload starting', {
      target: this.getUploadTarget(uploadUrl),
      mimeType,
      byteLength: base64Data.length,
    });

    try {
      if (Capacitor.isNativePlatform()) {
        await this.uploadReferenceAudioNative(uploadUrl, base64Data, mimeType);
        return;
      }

      await this.uploadReferenceAudioWeb(uploadUrl, base64Data, mimeType);
    } catch (error) {
      logger.error('Voice clone reference upload request failed', {
        error,
        target: this.getUploadTarget(uploadUrl),
        mimeType,
      });
      throw new Error(
        'Failed to upload reference audio before the storage service returned a response. This is often caused by a browser CORS/preflight failure or a network error.'
      );
    }
  }

  private async uploadReferenceAudioNative(
    uploadUrl: string,
    base64Data: string,
    mimeType: string
  ): Promise<void> {
    const response = await CapacitorHttp.put({
      url: uploadUrl,
      headers: {
        'Content-Type': mimeType,
      },
      data: base64Data,
      dataType: 'file',
    });

    if (response.status < 200 || response.status >= 300) {
      logger.warn('Voice clone reference upload returned non-OK response', {
        target: this.getUploadTarget(uploadUrl),
        mimeType,
        status: response.status,
      });
      throw new Error(`Failed to upload reference audio (HTTP ${response.status})`);
    }

    logger.info('Voice clone reference upload completed', {
      target: this.getUploadTarget(uploadUrl),
      mimeType,
      status: response.status,
      transport: 'capacitor-http',
    });
  }

  private async uploadReferenceAudioWeb(
    uploadUrl: string,
    base64Data: string,
    mimeType: string
  ): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': mimeType,
      },
      body: this.base64ToBlob(base64Data, mimeType),
    });

    if (!response.ok) {
      logger.warn('Voice clone reference upload returned non-OK response', {
        target: this.getUploadTarget(uploadUrl),
        mimeType,
        status: response.status,
        statusText: response.statusText,
      });
      throw new Error(`Failed to upload reference audio (HTTP ${response.status})`);
    }

    logger.info('Voice clone reference upload completed', {
      target: this.getUploadTarget(uploadUrl),
      mimeType,
      status: response.status,
      transport: 'fetch',
    });
  }

  private base64ToBlob(base64Data: string, mimeType: string): Blob {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let index = 0; index < byteCharacters.length; index += 1) {
      byteNumbers[index] = byteCharacters.charCodeAt(index);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }

  private getUploadTarget(uploadUrl: string): string {
    try {
      const parsed = new URL(uploadUrl);
      return `${parsed.origin}${parsed.pathname}`;
    } catch {
      return 'invalid-upload-url';
    }
  }

  private async waitForPublicVoiceCloneSample(
    voiceCloneRequestId: string,
    voiceCloneId: string,
    timeoutMs = 60_000,
    pollIntervalMs = 2_000
  ): Promise<VoiceCloneRequest> {
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      const request = await this.getPublicVoiceCloneRequest(voiceCloneRequestId);
      if (
        request.voiceCloneId === voiceCloneId &&
        request.previewDownloadUrl
      ) {
        return request;
      }

      if (request.status === 'Completed' && request.voiceCloneId === voiceCloneId) {
        return request;
      }

      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    throw new Error('Timed out waiting for the voice clone preview');
  }

  private waitForVoiceCloneSample(
    jwtToken: string,
    voiceCloneId: string,
    timeoutMs = 60_000,
    maxReconnects = 3
  ): VoiceCloneSampleWaitPromise {
    let resolveConnected!: () => void;
    let rejectConnected!: (reason?: unknown) => void;
    let isConnected = false;

    const connected = new Promise<void>((resolve, reject) => {
      resolveConnected = () => {
        if (isConnected) {
          return;
        }
        isConnected = true;
        resolve();
      };
      rejectConnected = reject;
    });

    const result = new Promise<VoiceCloneSampleReadyResponse>((resolve, reject) => {
      let reconnectCount = 0;
      let settled = false;
      let subscription: Subscription | undefined;
      let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
      const timeoutId = setTimeout(() => {
        rejectFailed('Timed out waiting for the voice clone preview');
      }, timeoutMs);

      const cleanup = () => {
        clearTimeout(timeoutId);
        if (subscription) {
          subscription.unsubscribe();
          subscription = undefined;
        }
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = undefined;
        }
        this.voiceCloneSocketService.disconnect();
      };

      const resolveReady = (payload: VoiceCloneSampleReadyResponse) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve(payload);
      };

      const rejectFailed = (message: string) => {
        if (!isConnected) {
          rejectConnected(new Error(message));
        }
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        reject(new Error(message));
      };

      const scheduleReconnect = () => {
        if (settled) {
          return;
        }

        cleanup();
        reconnectCount += 1;
        if (reconnectCount > maxReconnects) {
          rejectFailed('Voice clone preview websocket disconnected');
          return;
        }

        reconnectTimer = setTimeout(() => {
          connectSocket();
        }, 300);
      };

      const handleMessage = (message: VoiceCloneSocketMessage) => {
        switch (message.type) {
          case VoiceCloneWebSocketMessageType.CONNECTED:
            resolveConnected();
            return;
          case VoiceCloneWebSocketMessageType.SAMPLE_READY: {
            const payload = message.payload as VoiceCloneSampleReadyResponse;
            this.voiceCloneSocketService.acknowledge(message.notificationId);
            void this.syncNarratorsForSocketEvent(jwtToken);
            if (payload.voiceCloneId === voiceCloneId) {
              resolveReady(payload);
            }
            return;
          }
          case VoiceCloneWebSocketMessageType.SAMPLE_FAILED: {
            const payload = message.payload as VoiceCloneSampleFailedResponse;
            this.voiceCloneSocketService.acknowledge(message.notificationId);
            if (payload.voiceCloneId === voiceCloneId) {
              rejectFailed(payload.error);
            }
            return;
          }
        }
      };

      const connectSocket = () => {
        try {
          const socket$ = this.voiceCloneSocketService.connect(jwtToken);
          subscription = socket$.subscribe({
            next: (message) => handleMessage(message as VoiceCloneSocketMessage),
            error: () => scheduleReconnect(),
            complete: () => scheduleReconnect(),
          });
        } catch (error) {
          rejectFailed(this.getErrorMessage(error));
        }
      };

      connectSocket();
    }) as VoiceCloneSampleWaitPromise;

    result.connected = connected;
    return result;
  }

  private async syncNarratorsForSocketEvent(jwtToken: string): Promise<void> {
    try {
      await this.dexieNarratorsService.syncNarrators(jwtToken);
    } catch (error) {
      logger.warn('Voice clone narrator sync failed after websocket event', error);
    }
  }

  private getErrorMessage(error: unknown): string {
    if (
      error &&
      typeof error === 'object' &&
      'error' in error &&
      typeof error.error === 'object' &&
      error.error &&
      'error' in error.error &&
      typeof error.error.error === 'string'
    ) {
      return error.error.error;
    }

    if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string'
    ) {
      return error.message;
    }

    return 'Voice clone request failed';
  }

  private normalizeVoiceCloneError(
    error: unknown,
    fallbackMessage: string
  ): Error {
    const status =
      typeof error === 'object' && error && 'status' in error
        ? (error.status as number | undefined)
        : undefined;

    if (
      status === 403 &&
      fallbackMessage.includes('No active voice clone access')
    ) {
      return new VoiceCloneNoEntitlementError(fallbackMessage);
    }

    return new Error(fallbackMessage);
  }

}
